import React, { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { 
  Home, 
  Package, 
  Grid, 
  Users, 
  BadgeDollarSign, 
  Settings, 
  ChevronUp,
  List,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  PlusCircle,
  Calendar,
  CreditCard,
  Share2,
  BarChart,
  Link2
} from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";

// Add the animation keyframes as a CSS-in-JS style
const animations = `
  @keyframes slideUp {
    0% { transform: translateY(100%); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes slideDown {
    0% { transform: translateY(0); opacity: 1; }
    100% { transform: translateY(100%); opacity: 0; }
  }
  
  @keyframes fadeIn {
    0% { opacity: 0; }
    100% { opacity: 0.2; }
  }
  
  @keyframes fadeOut {
    0% { opacity: 0.2; }
    100% { opacity: 0; }
  }
`;

interface MobileBottomNavProps {
  activeView: string;
  onViewChange: (view: string) => void;
  activeSubmenu?: string;
  toggleSubmenu?: (menu: string) => void;
}

export const MobileBottomNav = ({ 
  activeView, 
  onViewChange, 
  activeSubmenu,
  toggleSubmenu 
}: MobileBottomNavProps) => {
  const { t } = useTranslation();
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [closingItem, setClosingItem] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const navItems = [
    { 
      label: t('navigation.settings'), 
      icon: <Settings className="h-4 w-4" />, 
      view: "settings", 
      hasSubmenu: true,
      submenuItems: [
        { label: t('navigation.general'), icon: <Settings className="h-4 w-4 mr-2" />, view: "settings/general" },
        { label: t('navigation.business'), icon: <BadgeDollarSign className="h-4 w-4 mr-2" />, view: "settings/business" },
        { label: t('navigation.notifications'), icon: <AlertCircle className="h-4 w-4 mr-2" />, view: "settings/notifications" },
        { label: 'Social Links', icon: <Link2 className="h-4 w-4 mr-2" />, view: "settings/social-links" },
        { label: t('navigation.payments'), icon: <CreditCard className="h-4 w-4 mr-2" />, view: "settings/payments" },
        { label: t('navigation.marketing'), icon: <BarChart className="h-4 w-4 mr-2" />, view: "settings/marketing" },
        { label: t('navigation.availability'), icon: <Calendar className="h-4 w-4 mr-2" />, view: "settings/availability" }
      ]
    },
    { 
      label: t('navigation.customers'), 
      icon: <Users className="h-4 w-4" />, 
      view: "customers" 
    },
    { 
      label: t('navigation.home'), 
      icon: <Home className="h-4 w-4" />, 
      view: "overview" 
    },
    { 
      label: t('navigation.requests'), 
      icon: <Grid className="h-4 w-4" />, 
      view: "service-requests", 
      hasSubmenu: true,
      submenuItems: [
        { label: t('navigation.calendar'), icon: <Calendar className="h-4 w-4 mr-2" />, view: "service-requests?view=calendar" },
        { label: t('navigation.upcoming'), icon: <Clock className="h-4 w-4 mr-2" />, view: "service-requests?view=upcoming" },
        { label: t('navigation.allRequests'), icon: <List className="h-4 w-4 mr-2" />, view: "service-requests?view=all" },
        { label: t('navigation.pending'), icon: <AlertCircle className="h-4 w-4 mr-2" />, view: "service-requests?view=pending" },
        { label: t('navigation.scheduled'), icon: <Clock className="h-4 w-4 mr-2" />, view: "service-requests?view=scheduled" },
        { label: t('navigation.completed'), icon: <CheckCircle className="h-4 w-4 mr-2" />, view: "service-requests?view=completed" },
        { label: t('navigation.rejected'), icon: <XCircle className="h-4 w-4 mr-2" />, view: "service-requests?view=rejected" },
        { label: t('navigation.create'), icon: <PlusCircle className="h-4 w-4 mr-2" />, view: "service-requests?view=create" }
      ]
    },
    { 
      label: t('navigation.services'), 
      icon: <Package className="h-4 w-4" />, 
      view: "services", 
      hasSubmenu: true,
      submenuItems: [
        { label: t('navigation.all'), icon: <List className="h-4 w-4 mr-2" />, view: "services" },
        { label: t('navigation.category'), icon: <Package className="h-4 w-4 mr-2" />, view: "categories" }
      ]
    }
  ];

  const handleItemClick = (item: any) => {
    if (item.hasSubmenu) {
      if (expandedItem === item.view) {
        // Start closing animation
        setIsClosing(true);
        setClosingItem(item.view);
        
        // Remove after animation completes
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
        }
        
        closeTimeoutRef.current = setTimeout(() => {
          setExpandedItem(null);
          setClosingItem(null);
          setIsClosing(false);
        }, 300); // Match animation duration
      } else {
        setIsClosing(false);
        setExpandedItem(item.view);
      }
      
      if (toggleSubmenu) {
        toggleSubmenu(item.view);
      }
    } else {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      setExpandedItem(null);
      setClosingItem(null);
      setIsClosing(false);
      onViewChange(item.view);
    }
  };

  const handleBackdropClick = () => {
    // Start closing animation
    setIsClosing(true);
    
    // Remove after animation completes
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    
    closeTimeoutRef.current = setTimeout(() => {
      setExpandedItem(null);
      setClosingItem(null);
      setIsClosing(false);
    }, 300); // Match animation duration
  };

  // Determine if we should show the submenu (either when expanded or when closing)
  const showSubmenu = expandedItem !== null || closingItem !== null;
  // Use the active item which is either the expanded one or the one that's closing
  const activeSubmenuItem = expandedItem || closingItem;

  return (
    <>
      {/* Add style element for the animation */}
      <style dangerouslySetInnerHTML={{ __html: animations }} />
      
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white z-50">
        {/* Gradient border line */}
        <div className="h-[2px] w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange"></div>
        <div className="grid grid-cols-5 items-center w-full">
          {navItems.map((item) => (
            <button
              key={item.view}
              onClick={() => handleItemClick(item)}
              className={cn(
                "flex flex-col items-center justify-center py-3 px-2 transition-all duration-200",
                activeView === item.view || 
                (item.view === "settings" && activeView === "settings") ||
                (item.hasSubmenu && (expandedItem === item.view || closingItem === item.view))
                  ? "text-gebeya-pink"
                  : "text-gray-500 hover:text-gray-700"
              )}
              aria-label={item.label}
            >
              <span className={cn(
                activeView === item.view || 
                (item.view === "settings" && activeView === "settings") ||
                (item.hasSubmenu && (expandedItem === item.view || closingItem === item.view))
                  ? "text-gebeya-pink"
                  : "text-gray-500"
              )}>
                {React.cloneElement(item.icon, { className: "h-5 w-5" })}
              </span>
              <span className="text-xs mt-1 truncate w-full text-center">{item.label}</span>
              {item.hasSubmenu && (expandedItem === item.view || closingItem === item.view) && (
                <ChevronUp className="h-3 w-3" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Submenu popup with animation */}
      {showSubmenu && (
        <div 
          className="md:hidden fixed bottom-14 left-0 right-0 bg-white shadow-lg z-50 overflow-hidden"
          style={{
            animation: isClosing 
              ? "slideDown 0.3s cubic-bezier(0.21, 1.02, 0.73, 1) forwards" 
              : "slideUp 0.3s cubic-bezier(0.21, 1.02, 0.73, 1) forwards"
          }}
        >
          {/* Gradient border line */}
          <div className="h-[2px] w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange"></div>
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {navItems.find(item => item.view === activeSubmenuItem)?.submenuItems?.map((subItem) => (
              <button
                key={subItem.view}
                onClick={() => {
                  onViewChange(activeSubmenuItem!);
                  
                  if (activeSubmenuItem === "settings") {
                    const section = subItem.view.split('/')[1];
                    if (section) {
                      navigate(`/dashboard/${subItem.view}`);
                    }
                  } else if (activeSubmenuItem === "service-requests" || activeSubmenuItem === "services") {
                      navigate(`/dashboard/${subItem.view}`);
                  }
                  
                  setExpandedItem(null);
                  setClosingItem(null);
                  setIsClosing(false);
                }}
                className="w-full text-left py-3 px-4 hover:bg-gray-100 rounded-md flex items-center"
              >
                {subItem.icon}
                <span>{subItem.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Backdrop for closing submenu by clicking anywhere */}
      {showSubmenu && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 md:hidden"
          style={{
            animation: isClosing 
              ? "fadeOut 0.2s ease-in forwards"
              : "fadeIn 0.2s ease-in forwards"
          }}
          onClick={handleBackdropClick}
        />
      )}
    </>
  );
}; 